import React, { useEffect, useState } from 'react';
import { FormCheckbox, FormGroup, FormRadioGroup, InPageAlert, Input } from '@snsw/react-component-library';
import PropTypes from 'prop-types';
import {
  isEmpty,
  isValidEmail,
  validateExt,
  validateFormattedAddress,
  validateMandatoryAndMaxLength,
  validateOptionalAndMaxLength,
} from 'shared/utils/Validation';
import { PlantDesignRegistrationHolderPage } from 'plantDesign/constants/plantDesignRegistrationHolderPage';
import { radioOptionsYesNo } from 'shared/constants/constants';
import styles from '../PlantDesignRegistrationHolder.module.scss';
import { ContactMethod } from '../common/ContactMethod';
import { LocalAndInternationalAddress } from 'shared/components/address/LocalAndInternationalAddress';
import { isRegisteredInAustraliaOutsideNSW } from '../common/utils';

const IndividualHolderFields = React.forwardRef((props, ref) => {
  const { value: storedHolderDetails = {}, onErrorChange = () => {}, onChange = () => {} } = props;
  const {
    isRegistrationHolder: storedIsRegistrationHolder = '',
    firstName: storedFirstName = '',
    middleName: storedMiddleName = '',
    lastName: storedLastName = '',
    emailAddress: storedEmailAddress = '',
    contactMethod: storedContactMethod = {},
    residentialAddress: storedResidentialAddress = {},
    isSameAddress: storedIsSameAddress = true,
    mailingAddress: storedMailingAddress = {},
    reasonToRegister: storedReasonToRegister = '',
  } = storedHolderDetails;

  const [isRegistrationHolder, setIsRegistrationHolder] = useState(storedIsRegistrationHolder);
  const [firstName, setFirstName] = useState(storedFirstName);
  const [middleName, setMiddleName] = useState(storedMiddleName);
  const [lastName, setLastName] = useState(storedLastName);
  const [emailAddress, setEmailAddress] = useState(storedEmailAddress);

  const [contactMethod, setContactMethod] = useState(storedContactMethod);
  const [residentialAddress, setResidientialAddress] = useState(storedResidentialAddress);
  const [isSameAddress, setIsSameAddress] = useState(storedIsSameAddress);
  const [mailingAddress, setMailingAddress] = useState(storedMailingAddress);
  const [reasonToRegister, setReasonToRegister] = useState(storedReasonToRegister);

  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [errorSummary, setErrorSummary] = useState([]);
  const [contactErrorSummary, setContactErrorSummary] = useState([]);
  const [residentAddrErrorSummary, setResidentAddrErrorSummary] = useState([]);
  const [mailingAddrErrorSummary, setMailingAddrErrorSummary] = useState([]);

  const validators = [
    {
      fieldName: 'isRegistrationHolder',
      fieldValue: isRegistrationHolder,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'firstName',
      fieldValue: firstName,
      isValid: (v) =>
        !isEmpty(v) || !isEmpty(lastName) || isEmpty(isRegistrationHolder) || isRegistrationHolder === 'yes',
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'lastName',
      fieldValue: lastName,
      isValid: (v) =>
        !isEmpty(v) || !isEmpty(firstName) || isEmpty(isRegistrationHolder) || isRegistrationHolder === 'yes',
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'emailAddress',
      fieldValue: emailAddress,
      isValid: (v) => isValidEmail(v) || isEmpty(isRegistrationHolder) || isRegistrationHolder === 'yes',
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter valid email address'),
    },
    {
      fieldName: 'reasonToRegister',
      fieldValue: reasonToRegister,
      isValid: (v) => !isEmpty(v) || !isRegisteredInAustraliaOutsideNSW(residentialAddress),
      errorMessage: () => 'Please enter details',
    },
  ];

  const validateStreetAddress = (street, isPoboxAllowed = false) => {
    const result = validateMandatoryAndMaxLength(street, 64);
    if (!result.status) return result;

    if (street.toLowerCase().startsWith('po box') && !isPoboxAllowed)
      return { status: false, errorMessage: 'PO Box address is not allowed' };

    return { status: true, errorMessage: '' };
  };

  const internationalAddressValidator = {
    validateCountry: (country) => validateMandatoryAndMaxLength(country, 40),
    validateStreet: validateStreetAddress,
    validateCity: (city) => validateOptionalAndMaxLength(city, 40),
    validateProvince: (province) => validateOptionalAndMaxLength(province, 40),
    validateZipCode: (zipCode) => validateOptionalAndMaxLength(zipCode, 12),
  };

  const localAddressValidator = {
    validateStreet: (street) => validateStreetAddress(street),
    validateSuburb: (suburb) => validateMandatoryAndMaxLength(suburb, 40),
    validateState: (state) => validateMandatoryAndMaxLength(state, 3),
    validatePostCode: (postCode) => validateMandatoryAndMaxLength(postCode, 4),
    validateFormattedAddress: (formattedAddress) => validateFormattedAddress(formattedAddress),
  };

  const localMailingAddressValidator = {
    ...localAddressValidator,
    validateStreet: (street) => validateStreetAddress(street, true),
    validateFormattedAddress: (formattedAddress) => validateFormattedAddress(formattedAddress, true),
  };

  const internationalMailingAddressValidator = {
    ...internationalAddressValidator,
    validateStreet: (street) => validateStreetAddress(street, true),
  };

  useEffect(() => {
    onErrorChange(errorSummary.concat(contactErrorSummary, residentAddrErrorSummary, mailingAddrErrorSummary));
  }, [errorSummary, contactErrorSummary, residentAddrErrorSummary, mailingAddrErrorSummary]);

  useEffect(() => {
    const holderDetails = {
      isRegistrationHolder,
      ...(isRegistrationHolder === 'no' && {
        firstName,
        middleName,
        lastName,
        emailAddress,
      }),
      contactMethod,
      residentialAddress,
      isSameAddress,
      ...(!isSameAddress && {
        mailingAddress,
      }),
      ...(isRegisteredInAustraliaOutsideNSW(residentialAddress) && {
        reasonToRegister,
      }),
    };

    onChange(holderDetails);
    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [
    isRegistrationHolder,
    firstName,
    middleName,
    lastName,
    emailAddress,
    contactMethod,
    residentialAddress,
    isSameAddress,
    mailingAddress,
    reasonToRegister,
  ]);

  const contactMethodRef = React.useRef();
  const residientialAddRef = React.useRef();
  const mailingAddRef = React.useRef();

  const validateAll = () => {
    const isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);
    const contactMethodValid = contactMethodRef.current.validate();
    const residentAddressValid = residientialAddRef.current.validate();
    let mailingAddressValid = true;

    if (mailingAddRef.current && !isSameAddress) mailingAddressValid = mailingAddRef.current.validate();

    setShowErrors(!isCurrentComponentValid);

    return isCurrentComponentValid && contactMethodValid && residentAddressValid && mailingAddressValid;
  };

  if (ref) {
    ref.current = {
      validate: () => {
        return validateAll();
      },
    };
  }

  return (
    <>
      <FormRadioGroup
        id="isRegistrationHolder"
        name="isRegistrationHolder"
        legend={PlantDesignRegistrationHolderPage.individualHolderIsRegistrationHolderLabel}
        helpMessage={PlantDesignRegistrationHolderPage.individualHolderIsRegistrationHolderHelpMessage}
        options={radioOptionsYesNo}
        onChange={setIsRegistrationHolder}
        value={isRegistrationHolder}
        errorMessage={errors?.isRegistrationHolder?.errorMessage}
        hasError={showErrors && errors?.isRegistrationHolder?.invalid}
        className={styles.marginTopThreeRem}
      />

      {isRegistrationHolder === 'no' && (
        <>
          <FormGroup
            id="firstName"
            label={PlantDesignRegistrationHolderPage.holderFirstNameLabel}
            errorMessage={errors?.firstName?.errorMessage}
            hasError={showErrors && errors?.firstName?.invalid}
            margin={{ top: 'lg' }}
          >
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} inputWidth="lg" />
          </FormGroup>

          <FormGroup
            id="middleName"
            label={PlantDesignRegistrationHolderPage.holderMiddleNameLabel}
            errorMessage={errors?.middleName?.errorMessage}
            hasError={showErrors && errors?.middleName?.invalid}
            margin={{ top: 'sm' }}
          >
            <Input value={middleName} onChange={(e) => setMiddleName(e.target.value)} inputWidth="lg" />
          </FormGroup>

          <FormGroup
            id="lastName"
            label={PlantDesignRegistrationHolderPage.holderLastNameLabel}
            errorMessage={errors?.lastName?.errorMessage}
            hasError={showErrors && errors?.lastName?.invalid}
            margin={{ top: 'sm' }}
          >
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} inputWidth="lg" />
          </FormGroup>

          <FormGroup
            id="emailAddress"
            label={PlantDesignRegistrationHolderPage.holderEmailAddressLabel}
            errorMessage={errors?.emailAddress?.errorMessage}
            hasError={showErrors && errors?.emailAddress?.invalid}
            margin={{ top: 'sm' }}
          >
            <Input value={emailAddress} onChange={(e) => setEmailAddress(e.target.value)} inputWidth="xl" />
          </FormGroup>
        </>
      )}

      <ContactMethod
        id="contactMethod"
        ref={contactMethodRef}
        onChange={setContactMethod}
        value={contactMethod}
        onErrorChange={setContactErrorSummary}
      />

      <LocalAndInternationalAddress
        id="residential"
        margin={{ top: 'xl' }}
        baseUrl={`${process.env.REACT_APP_BASE_URL}${process.env.REACT_APP_URL_PREFIX}/addressApi/plant-design`}
        legend="Residential address"
        onChange={setResidientialAddress}
        value={residentialAddress}
        ref={residientialAddRef}
        onErrorChange={setResidentAddrErrorSummary}
        internationalAddressValidator={internationalAddressValidator}
        localAddressValidator={localAddressValidator}
        helpMessage="This cannot be a PO Box"
      />

      {isRegisteredInAustraliaOutsideNSW(residentialAddress) && (
        <>
          <InPageAlert
            className={styles.marginTop2rem}
            variant="warning"
            title="We recommend registering this design with your local state regulator."
          >
            <p>
              You can register this design in NSW, but you will not be eligible for a refund if your application is
              declined.
            </p>
          </InPageAlert>
          <FormGroup
            id="reasonToRegister"
            label={PlantDesignRegistrationHolderPage.reasonToRegisterInNSWLabel}
            errorMessage={errors?.reasonToRegister?.errorMessage}
            hasError={showErrors && errors?.reasonToRegister?.invalid}
            margin={{ top: 'md' }}
          >
            <Input value={reasonToRegister} onChange={(e) => setReasonToRegister(e.target.value)} />
          </FormGroup>
        </>
      )}

      {residentialAddress?.addressType && (
        <>
          <span className={styles.labelMsg + ' ' + styles.marginTopOneHalf}>Mailing address</span>
          <FormCheckbox
            id={`addSecondary`}
            name={`addSecondary`}
            label={PlantDesignRegistrationHolderPage.isMailingAddressSameAsResidentialAddressLabel}
            onChange={() => {
              setIsSameAddress(!isSameAddress);
              setMailingAddress({});
              setMailingAddrErrorSummary([]);
            }}
            checked={isSameAddress}
            value="mailingAddress"
            margin={{ top: 'sm' }}
          />
          {!isSameAddress && (
            <LocalAndInternationalAddress
              id="mailing"
              margin={{ top: 'sm' }}
              baseUrl={`${process.env.REACT_APP_BASE_URL}${process.env.REACT_APP_URL_PREFIX}/addressApi/plant-design`}
              legend=" "
              onChange={setMailingAddress}
              value={mailingAddress}
              ref={mailingAddRef}
              onErrorChange={setMailingAddrErrorSummary}
              internationalAddressValidator={internationalMailingAddressValidator}
              localAddressValidator={localMailingAddressValidator}
              helpMessage=" "
            />
          )}
        </>
      )}
    </>
  );
});

IndividualHolderFields.displayName = 'IndividualHolderFields';
IndividualHolderFields.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.object,
};

IndividualHolderFields.defaultProps = {
  onChange: null,
  value: null,
};

export { IndividualHolderFields };
